import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Navbar } from '../components/layout/Navbar';
import { InteractiveMap } from '../components/InteractiveMap';
import { SiteMapOverlay } from '../components/SiteMapOverlay';
import { AdminLayout } from '../components/layout/AdminLayoutNew';
import { useAuth } from '../context/AuthContext';
import { Home, CheckCircle, Clock, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Project logos and images mapping
const projectLogos = {
  'bakrajo-hills': '/branding/bakrajo_logo.png',
};

const projectImages = {
  'bakrajo-hills': '/branding/hero.jpg',
};

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [units, setUnits] = useState([]);
  const [sitemapStats, setSitemapStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated: isAdminAuth } = useAuth();

  const isBakrajoHills = projectId === 'bakrajo-hills';

  useEffect(() => {
    let cancelled = false;

    const fetchProjectData = async () => {
      try {
        const requests = [
          axios.get(`${API_URL}/api/projects/${projectId}`),
          axios.get(`${API_URL}/api/units?project_id=${projectId}`)
        ];
        
        if (isBakrajoHills) {
          requests.push(axios.get(`${API_URL}/api/sitemap-units/${projectId}`));
        }
        
        const results = await Promise.all(requests);
        if (cancelled) return;
        setProject(results[0].data);
        setUnits(results[1].data);
        
        if (isBakrajoHills && results[2]) {
          const smData = results[2].data;
          const allUnits = [...(smData.units || []), ...(smData.units_off_map || [])];
          const soldCount = allUnits.filter(u => (u.status || '').toLowerCase() === 'sold').length;
          const pendingCount = allUnits.filter(u => (u.status || '').toLowerCase() === 'pending').length;
          const companyCount = allUnits.filter(u => (u.status || '').toLowerCase() === 'company_buyer').length;
          setSitemapStats({
            total: smData.total,
            sold: soldCount,
            pending: pendingCount,
            company_buyer: companyCount,
            available: smData.total - soldCount - pendingCount - companyCount,
          });
        }
      } catch (error) {
        console.error('Error fetching project:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchProjectData();

    // Auto-refresh stats on tab focus + 30s polling (sync with admin edits)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchProjectData();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', fetchProjectData);
    const interval = setInterval(fetchProjectData, 30000);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', fetchProjectData);
      clearInterval(interval);
    };
  }, [projectId, isBakrajoHills]);

  const unitStats = isBakrajoHills && sitemapStats ? {
    total: sitemapStats.total,
    available: sitemapStats.available,
    sold: sitemapStats.sold,
    pending: sitemapStats.pending,
  } : {
    total: units.length,
    available: units.filter(u => u.status === 'available').length,
    sold: units.filter(u => u.status === 'sold').length,
    pending: units.filter(u => u.status === 'pending').length,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border border-white/20 border-t-white rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-white text-2xl font-light mb-6">Project not found</h2>
          <Link to="/projects" className="btn-outline">
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  return (
    <ConditionalAdminWrapper isAdmin={isAdminAuth}>
      <div className="min-h-screen bg-black text-white" data-testid="project-detail-page">
        {!isAdminAuth && <Navbar />}

      {/* Hero Section */}
      <section className="relative h-[60vh] lg:h-[70vh] flex items-end overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={projectImages[project.id] || project.thumbnail || 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1920&q=80'}
            alt={project.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/30" />
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-6 lg:px-12 pb-16 lg:pb-24">
          {/* Project Logo */}
          {projectLogos[project.id] && (
            <img
              src={projectLogos[project.id]}
              alt={project.name}
              className="h-24 lg:h-36 w-auto mb-6 animate-fade-in-up"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}

          {/* Location & Status */}
          <div className="flex flex-wrap items-center gap-4 mb-4 animate-fade-in-up delay-100">
            <p className="text-xs uppercase tracking-[0.2em] text-white/50 font-light">
              {project.location}
            </p>
            <span className={`inline-block px-4 py-1 text-[10px] uppercase tracking-widest font-light ${
              project.status === 'completed' 
                ? 'bg-white text-black' 
                : 'bg-white/10 text-white border border-white/30'
            }`}>
              {project.status === 'active' ? 'Ongoing' : project.status === 'completed' ? 'Completed' : 'Upcoming'}
            </span>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="border-y border-white/10 bg-[#0a0a0a]" data-testid="unit-stats">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px">
            <div className="flex items-center gap-4 py-6 lg:py-8">
              <div className="w-12 h-12 flex items-center justify-center border border-white/20">
                <Home className="w-5 h-5 text-white/60" />
              </div>
              <div>
                <p className="text-2xl lg:text-3xl font-light text-white">{unitStats.total}</p>
                <p className="text-[10px] uppercase tracking-[0.15em] text-white/40 font-light">Total Units</p>
              </div>
            </div>
            <div className="flex items-center gap-4 py-6 lg:py-8">
              <div className="w-12 h-12 flex items-center justify-center border border-green-500/30 bg-green-500/10">
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl lg:text-3xl font-light text-green-500">{unitStats.available}</p>
                <p className="text-[10px] uppercase tracking-[0.15em] text-white/40 font-light">Available</p>
              </div>
            </div>
            <div className="flex items-center gap-4 py-6 lg:py-8">
              <div className="w-12 h-12 flex items-center justify-center border border-orange-500/30 bg-orange-500/10">
                <Clock className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl lg:text-3xl font-light text-orange-500">{unitStats.pending}</p>
                <p className="text-[10px] uppercase tracking-[0.15em] text-white/40 font-light">Pending</p>
              </div>
            </div>
            <div className="flex items-center gap-4 py-6 lg:py-8">
              <div className="w-12 h-12 flex items-center justify-center border border-red-500/30 bg-red-500/10">
                <XCircle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-2xl lg:text-3xl font-light text-red-500">{unitStats.sold}</p>
                <p className="text-[10px] uppercase tracking-[0.15em] text-white/40 font-light">Sold</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Map Section */}
      <section className="py-16 lg:py-24 bg-black" data-testid="map-section">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4 font-light">
              UNIT AVAILABILITY
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight mb-4">
              Select a Unit
            </h2>
            <p className="text-white/50 font-light">
              Click on any marker to view unit details. Colors indicate availability status.
            </p>
          </div>

          {isBakrajoHills ? (
            <SiteMapOverlay 
              mapImage="/maps/bakrajo_hills_siteplan_v2.jpg"
              projectId={projectId}
            />
          ) : (
            <InteractiveMap 
              mapImage={project.map_image || projectImages[project.id]} 
              units={units} 
            />
          )}
        </div>
      </section>

      </div>
    </ConditionalAdminWrapper>
  );
}

function ConditionalAdminWrapper({ isAdmin, children }) {
  if (isAdmin) {
    return <AdminLayout bare>{children}</AdminLayout>;
  }
  return children;
}
